import { useState, useEffect } from "react";
import axios from "axios";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import HeroSection from "@/components/HeroSection";
import BooksSection from "@/components/BooksSection";
import ReviewsSection from "@/components/ReviewsSection";
import AboutSection from "@/components/AboutSection";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import FAQSection, { FAQ_DATA } from "@/components/FAQSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_LINKS = [
  { label: "Books", href: "#books" },
  { label: "Reviews", href: "#reviews" },
  { label: "About", href: "#about" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function LandingPage() {
  const [books, setBooks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/books`).then(r => setBooks(r.data)).catch(() => {});
    axios.get(`${API}/reviews`).then(r => setReviews(r.data)).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // SEO: Inject dynamic JSON-LD for books, reviews, and FAQ
  useEffect(() => {
    if (books.length === 0) return;
    const bookSchemas = books.map(book => ({
      "@type": "Book", name: book.title,
      author: { "@type": "Person", name: "Derek Fee" },
      image: book.cover_url, url: book.amazon_link,
      bookFormat: "EBook", inLanguage: "en",
      genre: ["Crime Fiction", "Thriller", "Detective Fiction"],
      ...(book.series !== "Others" && { isPartOf: { "@type": "BookSeries", name: book.series === "Belfast Murders" ? "The Belfast Murders" : "The Galway Murders" } })
    }));
    const reviewSchemas = reviews.map(r => ({
      "@type": "Review", reviewBody: r.quote,
      author: { "@type": "Person", name: r.reviewer_name },
      itemReviewed: { "@type": "Book", name: r.book_title, author: { "@type": "Person", name: "Derek Fee" } }
    }));
    const faqSchema = {
      "@type": "FAQPage",
      mainEntity: FAQ_DATA.map(f => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } }))
    };
    const jsonLd = { "@context": "https://schema.org", "@graph": [...bookSchemas, ...reviewSchemas, faqSchema] };
    let script = document.getElementById("dynamic-seo-jsonld");
    if (!script) { script = document.createElement("script"); script.type = "application/ld+json"; script.id = "dynamic-seo-jsonld"; document.head.appendChild(script); }
    script.textContent = JSON.stringify(jsonLd);
    return () => { const el = document.getElementById("dynamic-seo-jsonld"); if (el) el.remove(); };
  }, [books, reviews]);

  const latestBook = books.find(b => b.is_latest) || books[0];

  return (
    <div data-testid="landing-page" className="text-white">
      {/* Navbar */}
      <header>
        <nav
          data-testid="navbar"
          className={`fixed top-0 left-0 right-0 z-50 py-4 transition-colors duration-300 ${
            scrolled ? "nav-scrolled" : ""
          }`}
        >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl flex justify-between items-center">
          <a href="#" className="font-display text-2xl font-bold tracking-tight text-white">
            Derek Fee
          </a>
          <div className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map(l => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="text-zinc-400 hover:text-white transition-colors text-sm uppercase tracking-widest"
              >
                {l.label}
              </a>
            ))}
          </div>
          <div className="md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5 text-white" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0a0a0a] border-zinc-800 w-64">
                <div className="flex flex-col gap-6 mt-12">
                  {NAV_LINKS.map(l => (
                    <a
                      key={l.href}
                      href={l.href}
                      onClick={() => setMobileOpen(false)}
                      className="text-zinc-300 hover:text-white text-lg font-display"
                    >
                      {l.label}
                    </a>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
      </header>

      <main>
        <HeroSection latestBook={latestBook} />
        <BooksSection books={books} />
        <ReviewsSection reviews={reviews} />
        <AboutSection />
        <ContactSection />
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
}
